import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useI18n, LangToggle } from "@/context/I18nContext";

const LEVELS = ["BEGINNER", "INTERMEDIATE", "EXPERT"];
const INPUT_CLS = "w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]";

// Module-level so its component identity is stable across re-renders (keeps the input mounted while typing).
const Field = ({ k, label, type = "text", required, value, onChange, t }) => (
  <div>
    <label className="errah-label mb-1 block">{t(label)}{required && " *"}</label>
    <input type={type} value={value} onChange={(e) => onChange(k, e.target.value)} data-testid={`apply-${k}`} className={INPUT_CLS} />
  </div>
);

export default function ApplicatorApply() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const locState = useLocation().state || {};
  const verifiedToken = locState.verified_token || null;
  const ref = params.get("ref") || "";
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState({}); // service_id -> {experience_level, years}
  const [form, setForm] = useState({
    name: "", email: "", password: "", mobile: locState.mobile || "", whatsapp: "", city: "", pincode: "", address: "",
    profession: "", experience_years: "", current_business: "", technicians: "", service_area: "", referral_code: ref,
  });

  useEffect(() => { api.get("/applicator/services/catalogue").then(({ data }) => setCatalogue(data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const byCat = catalogue.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});
  const toggleSvc = (id) => setSelected((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = { experience_level: "INTERMEDIATE", years: 1 }; return n; });

  const submit = async () => {
    if (!form.name || !form.mobile || !form.city) { toast.error("Please fill required fields"); setStep(1); return; }
    if (!verifiedToken && (!form.email || !form.password)) { toast.error("Email and password required"); setStep(1); return; }
    setBusy(true);
    try {
      const services = Object.entries(selected).map(([service_id, v]) => ({ service_id, experience_level: v.experience_level, years: Number(v.years) || 0 }));
      const { data } = await api.post("/applicator/apply", {
        ...form, email: form.email || null, password: form.password || null,
        mobile_verified_token: verifiedToken,
        experience_years: Number(form.experience_years) || null, technicians: Number(form.technicians) || null,
        language: localStorage.getItem("errah_lang") || "en", services,
      });
      toast.success(`Application submitted — ${data.applicator_code}`);
      navigate("/applicator/login");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit application"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="applicator-apply-page">
      <Seo title="Apply — ERRĀH Applicator Network" description="Apply to join the ERRĀH Certified Applicator Network." />
      <div className="glass"><div className="max-w-[900px] mx-auto px-5 h-[60px] flex items-center justify-between">
        <Link to="/applicator-network" className="flex items-center gap-2 text-sm text-gray-300"><ArrowLeft size={16} /> {t("back_to_site")}</Link>
        <LangToggle />
      </div></div>

      <div className="max-w-[900px] mx-auto px-5 py-12">
        <p className="errah-label gold-text mb-2">{t("join_network")}</p>
        <h1 className="serif-head text-3xl lg:text-5xl mb-2">{t("apply")}</h1>
        {ref && <p className="text-sm text-gray-400 mb-6" data-testid="apply-ref">{t("referral_code")}: <span className="gold-text">{ref}</span></p>}

        <div className="flex gap-2 mb-8" data-testid="apply-steps">
          {[t("personal"), t("professional"), t("skills")].map((s, i) => (
            <div key={i} className={`flex-1 py-2 text-center text-xs tracking-wide border ${step === i + 1 ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/10 text-gray-500"}`}>{String(i + 1).padStart(2, "0")} {s}</div>
          ))}
        </div>

        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="apply-step-1">
            <Field k="name" label="full_name" required value={form.name} onChange={set} t={t} />
            <Field k="mobile" label="mobile" required value={form.mobile} onChange={set} t={t} />
            <Field k="whatsapp" label="whatsapp" value={form.whatsapp} onChange={set} t={t} />
            <Field k="email" label="email" type="email" required={!verifiedToken} value={form.email} onChange={set} t={t} />
            <Field k="password" label="password" type="password" required={!verifiedToken} value={form.password} onChange={set} t={t} />
            <Field k="city" label="city" required value={form.city} onChange={set} t={t} />
            <Field k="pincode" label="pincode" value={form.pincode} onChange={set} t={t} />
            <Field k="address" label="address" value={form.address} onChange={set} t={t} />
          </div>
        )}
        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="apply-step-2">
            <Field k="profession" label="profession" value={form.profession} onChange={set} t={t} />
            <Field k="experience_years" label="experience_years" type="number" value={form.experience_years} onChange={set} t={t} />
            <Field k="current_business" label="current_business" value={form.current_business} onChange={set} t={t} />
            <Field k="technicians" label="technicians" type="number" value={form.technicians} onChange={set} t={t} />
            <Field k="service_area" label="service_area" value={form.service_area} onChange={set} t={t} />
          </div>
        )}
        {step === 3 && (
          <div data-testid="apply-step-3">
            <p className="text-sm text-gray-300 mb-4">{t("select_services")}</p>
            {Object.entries(byCat).map(([cat, list]) => (
              <div key={cat} className="mb-6">
                <p className="errah-label gold-text mb-2">{cat}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {list.map((s) => (
                    <div key={s.id} className={`hair-border p-3 ${selected[s.id] ? "bg-[#C9A45C]/10 border-[#C9A45C]/50" : "bg-white/[0.02]"}`}>
                      <button onClick={() => toggleSvc(s.id)} data-testid={`apply-svc-${s.id}`} className="w-full flex items-center justify-between text-left">
                        <span className="text-sm">{s.name}</span>
                        {selected[s.id] && <Check size={16} className="text-[#C9A45C]" />}
                      </button>
                      {selected[s.id] && (
                        <div className="mt-3 flex gap-2">
                          <select value={selected[s.id].experience_level} onChange={(e) => setSelected((x) => ({ ...x, [s.id]: { ...x[s.id], experience_level: e.target.value } }))}
                            className="flex-1 bg-[#111] border border-white/15 px-2 py-1.5 text-xs">
                            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                          <input type="number" min="0" value={selected[s.id].years} placeholder={t("years")}
                            onChange={(e) => setSelected((x) => ({ ...x, [s.id]: { ...x[s.id], years: e.target.value } }))}
                            className="w-20 bg-[#111] border border-white/15 px-2 py-1.5 text-xs" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between mt-8">
          {step > 1 ? <button onClick={() => setStep(step - 1)} data-testid="apply-back" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-sm hover:bg-white hover:text-black transition-colors"><ArrowLeft size={15} /> {t("back")}</button> : <span />}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} data-testid="apply-next" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{t("next")} <ArrowRight size={15} /></button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="apply-submit" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">{busy ? "…" : t("submit")} <ArrowRight size={15} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
